import React, { useEffect, useRef, useState } from "react";
import { ExtendedNodeData, find, RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCIcon, RCCRefHierarchicalTree, RCCServicesApi, RCCTooltip, removeNode, toggleExpandedForAll } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import LoadingContainer from "src/components/LoadingContainer";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { ITypeFinancialManagement } from "src/pages/FinancialManagement/interface";
import IModalRefPermissionsFinancialManagement from "src/components/HierarchyFinancialManagement/ModalPermissions/interface";
import ModalPermissionsFinancialManagement from "src/components/HierarchyFinancialManagement/ModalPermissions";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { useHistory } from "react-router-dom";
import ModalAddOrChangeSubsidiary from "./modalAddOrChange";
import { IHierarchy, IModalRefSubsidiaryFinancialManagement } from "./interface";
import * as HierarchyStyles from "./styles";

const HierarchyNode: React.FC<ExtendedNodeData> = ({
	node,
	path,
	parentNode,
	deleteItem,
	openModalPermissions,
	openModalAdd,
	openModalChange
}) => {
	const {translate} = useTranslation();
	const {administrator, superUser} = useAuth();
	return(
		<HierarchyStyles.HierarchyDivContainer>
			<HierarchyStyles.Title title={node.name}>
				{node.name} {node.integrationCode && node.integrationCode !== null && node.integrationCode.length > 0 && `(${node.integrationCode})` }
			</HierarchyStyles.Title> 
			<HierarchyStyles.StyledDiv>
				{(administrator || superUser || node.permission === "1") && (
					<OptionsColumn styleInline="font-weight: 500;" position="leftEnd">
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<>
								{!node.hasValuesInserted && (
									<RCCDropDownItem selfEnclose onClick={() => openModalAdd(node)}>
										<RCCIcon name="plus"/>
										{translate("Adicionar")}
									</RCCDropDownItem>
								)}
								<RCCDropDownItem selfEnclose onClick={() => openModalPermissions(node)}>
									<RCCIcon name="lock" />
									{translate("Permissões")}
								</RCCDropDownItem>	
								<RCCDropDownItem selfEnclose onClick={() => openModalChange(node, parentNode)}>
									<RCCIcon name="pencil" />
									{translate("Alterar")}
								</RCCDropDownItem>
							</>
						</CheckPermission>
						{parentNode !== null && (
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCDropDownItem selfEnclose onClick={() => deleteItem(node.id, node.name, path)} >
									<RCCIcon name="trash" />
									{translate("Excluir")}
								</RCCDropDownItem>
							</CheckPermission>
						)}
					</OptionsColumn>
				)}
			</HierarchyStyles.StyledDiv>
		</HierarchyStyles.HierarchyDivContainer>
	);};


const HierarchyFinancialManagementSubsidiary: React.FC<IHierarchy> = ({
	ofa001Id,
	title
}) => {
	const history = useHistory();
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [hierarchy, setHierarchy] = useState<any[]>([]);
	const oldHierarchy = useRef<any[]>([]);
	const [loading, setLoading] = useState(true);
	const getNodeKeyFunction = ({node}:any) => node.id;
	const [expanded, setExpanded] = useState(false);
	const selectedLineId = useRef<number | undefined>(undefined);

	const refHierarchicalTree = useRef<RCCRefHierarchicalTree>(null);
	const refModalAddOrChange = useRef<IModalRefSubsidiaryFinancialManagement>(null);
	const refModalPermissions = useRef<IModalRefPermissionsFinancialManagement>(null);
    
	const getHierarchy = async () => {      
		try{           
			setLoading(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "Subsidiary/ListHierarchy",
				urlParameters: {
					ofa001Id
				}
			});

			let hierarchyData = [...data];
			if(!selectedLineId.current){
				selectedLineId.current = data.length === 0 ? undefined : data[0].id;
				hierarchyData = toggleExpandedForAll({
					treeData:[...data], expanded: true
				});
				setExpanded(true);
			}
			else{
				hierarchyData = find({
					getNodeKey:getNodeKeyFunction, 
					expandAllMatchPaths: true,
					treeData: [...data],
					searchMethod:(searchData:any) => searchData.node.id === selectedLineId.current}
				)?.treeData;
			}

			setHierarchy(hierarchyData);
			oldHierarchy.current = data;
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os itens"), addToast);
			setLoading(false);
		}
	};

	const handleTreeMove = async(data:any) => {
		const oldHierarchyValue = [...oldHierarchy.current];
		try {
			const { nextParentNode } = data;
			RCCServicesApi.putRequest({
				method: "FinancialManagement/UpdateOrderInHierarchy",// methodUpdateOrder,
				body: {
					parentId: nextParentNode.id,
					children: nextParentNode.children.map((x: any) => x.id),
					type: ITypeFinancialManagement.SUBSIDIARY
				}
			});

			oldHierarchy.current = hierarchy;
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao atualizar hierarquia"), addToast);
			setHierarchy(oldHierarchyValue);
		}
	};

	const removeItem = (path: Array<string|number>) => {
		const data = removeNode({ treeData:[...hierarchy], path, getNodeKey:getNodeKeyFunction });
		oldHierarchy.current = data?.treeData||[];
		setHierarchy(data?.treeData||[]);
	};

	const deleteItem = async(id: number, name: string, path: Array<string|number>) => {
		confirm(
			translate("Exclusão"),
			`${translate("Deseja realmente excluir")} ${name}?`,
			async () => {
				setLoading(true);
				await RCCServicesApi.deleteRequest({
					method: "FinancialManagement/DeleteInHierarchy",// methodDelete,
					urlParameters: {
						id,
						type: ITypeFinancialManagement.SUBSIDIARY
					}
				});			
				removeItem(path);
				addToast({
					color: "green",
					description: translate("Item excluído com sucesso"),
					title: translate("Sucesso")
				});
				setLoading(false);
			},
			(err: any) => {
				standardErrorMessages(err, translate("Erro ao excluir o item"), addToast);
				setLoading(false);
			},
			true
		);
	};

	const expandAll = () => {
		const expandedData = toggleExpandedForAll({treeData:[...hierarchy], expanded: !expanded});
		setHierarchy(expandedData);
		setExpanded(!expanded);
	};


	useEffect(() => {
		getHierarchy();
	}, []);

	return (
		<>
			<RCCGrid container item direction="column" style={{ display: "flex", width: "100%", flex: 1}}>
				<RCCGrid container item justifyContent="space-between" alignItems="center">
					<Title
						color="purple"
					>
						{title}
					</Title>
					<HierarchyStyles.StyledDiv>
						<RCCTooltip
							color="grey"
							text={translate("Expandir/Comprimir")}
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: expanded ? "compress" : "expand",
									color: "var(--default-color-font)",
									size: 16
								}}
								onClick={expandAll}
							/>
						</RCCTooltip>
						<RCCTooltip
							color="grey"
							text={translate("Pesquisar")}
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: "search",
									color: "var(--default-color-font)",
									size: 16
								}}
								onClick={() => refHierarchicalTree.current?.toggleSearch()}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Voltar")}
							color="grey"
						>
							<RCCButton
								icon={{name: "arrowBack", color: "#FFF"}}
								color="grey"
								layout="isRounded"
								size="md"
								onClick={() => history.push(`/financialmanagement/panel/${ofa001Id}`)}
							/>
						</RCCTooltip>
					</HierarchyStyles.StyledDiv>
				</RCCGrid>
				<LoadingContainer loading={loading} styleInline="width: 100%; >div:last-child{ display: flex; width: 100%; flex: 1; }">
					{hierarchy && hierarchy.length === 0 ? (
						<RCCAlert
							title={translate("Atenção")}
							description={translate("Não foram encontrados dados")}
						/>
					): (
						<RCCHierarchicalTree
							data={hierarchy}
							styleInline="width: 100%; .rst__tree{ height: 100%; padding-bottom: 100px; }"
							ref={refHierarchicalTree}
							isSearchable={{
								searchKeyOfNode: "name",
								placeholderInput: translate("Pesquisar")
							}}
							getNodeKey={getNodeKeyFunction}
							onChange={(treeData) => setHierarchy(treeData)}
							scaffoldBlockPxWidth={24}
							generateNodeProps={(row:any) => ({
								title: <HierarchyNode 
									node={row.node}
									path={row.path} 
									treeIndex={row.treeIndex} 
									lowerSiblingCounts={row.lowerSiblingCounts} 
									isSearchMatch={row.isSearchMatch}
									isSearchFocus={row.isSearchFocus}
									parentNode={row.parentNode}
									deleteItem={deleteItem}
									openModalPermissions={(node: any) => {
										if(!refModalPermissions.current)
											return;
										selectedLineId.current = node.id;
										refModalPermissions.current.openModalPermissionsFinancialManagement(node.id, node.name, Number(ofa001Id), getHierarchy);
									}}
									openModalAdd={(node: any) => {
										if(!refModalAddOrChange.current)
											return;
										selectedLineId.current = node.id;
										refModalAddOrChange.current.openModalAddOrChange(node.id, undefined, undefined, false, false, getHierarchy);
									}}
									openModalChange={(node: any, parentNode: any) => {
										if(!refModalAddOrChange.current)
											return;
										selectedLineId.current = node.id;
										refModalAddOrChange.current.openModalAddOrChange(parentNode !== null ? parentNode.id : 0, node.id, node.name, parentNode === null, node.hasChildren, getHierarchy);
									}}
								/>
							})}
							onMoveNode={handleTreeMove}
							canDrop={(data: any) => data.prevParent && data.prevParent !== null && data.nextParent && data.nextParent !== null && data.prevParent.id === data.nextParent.id}
							canDrag={(data: any) => data.parentNode !== null}
						/>
					)}
				</LoadingContainer>
			</RCCGrid>
			<ModalPermissionsFinancialManagement 
				ref={refModalPermissions}
				type={ITypeFinancialManagement.SUBSIDIARY}
			/>
			<ModalAddOrChangeSubsidiary 
				ref={refModalAddOrChange}
				ofa001Id={ofa001Id}
			/>
		</>
	);
};

export default HierarchyFinancialManagementSubsidiary;
